import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminJobPostings = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    is_active: '',
    department: '',
    employment_type: '',
    search: '',
    ...initialFilters
  });

  const cleanFilters = useMemo(() => {
    const cleaned = Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
    if (!cleaned.per_page) {
      cleaned.per_page = 10;
    }
    return cleaned;
  }, [filters]);

  const queryString = new URLSearchParams(cleanFilters).toString();

  const { data: jobsResponse, isLoading, error } = useQuery({
    queryKey: ['adminJobPostings', cleanFilters],
    queryFn: async () => {
      const response = await api.get(`/admin/job-postings${queryString ? `?${queryString}` : ''}`);
      return response.data;
    }
  });

  const { data: statisticsResponse } = useQuery({
    queryKey: ['adminJobPostingStatistics'],
    queryFn: async () => {
      const response = await api.get('/admin/job-postings/statistics');
      return response.data;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/job-postings', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminJobPostings']);
      queryClient.invalidateQueries(['adminJobPostingStatistics']);
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/job-postings/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminJobPostings']);
      queryClient.invalidateQueries(['adminJobPostingStatistics']);
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.patch(`/admin/job-postings/${id}/toggle-status`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminJobPostings']);
      queryClient.invalidateQueries(['adminJobPostingStatistics']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/job-postings/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminJobPostings']);
      queryClient.invalidateQueries(['adminJobPostingStatistics']);
    }
  });

  const jobs = useMemo(() => {
    if (jobsResponse?.data?.data) {
      return jobsResponse.data.data;
    }
    return jobsResponse?.data || [];
  }, [jobsResponse]);

  const statistics = useMemo(() => {
    return statisticsResponse?.data || {
      total_jobs: 0,
      active_jobs: 0,
      inactive_jobs: 0,
      total_applications: 0
    };
  }, [statisticsResponse]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return {
    jobs,
    isLoading,
    error,
    filters,
    handleFilterChange,
    statistics,
    createJob: (data, options) => createMutation.mutate(data, options),
    isCreating: createMutation.isPending,
    updateJob: (data, options) => updateMutation.mutate(data, options),
    isUpdating: updateMutation.isPending,
    toggleStatus: (id, options) => toggleStatusMutation.mutate(id, options),
    isToggling: toggleStatusMutation.isPending,
    deleteJob: (id, options) => deleteMutation.mutate(id, options),
    isDeleting: deleteMutation.isPending
  };
};

